import { Button, message, Table, Typography } from 'antd';
import type { CheckboxProps } from 'antd/es/checkbox';
import type { ColumnsType } from 'antd/es/table';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BagStatus, getBagStatusText } from '../../bags/graphql/types/BagTagType';
import { BAG_STATUSES, CREATION_METHODS, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../constants';
import { toPrintJobs } from '../mock/batchMockActions';
import { MoveSource } from '../mock/batchMockDerive';
import { BatchBag, BatchDirection, PrintJob, SortingBatch } from '../types';
import { blockedReasonOf } from '../utils/batchRules';
import { formatCount } from '../utils/formatUtils';
import { BagStatusTag, CreationMethodTag } from './BatchTags';
import { CreateBagsTarget } from './overlayProps';

const NS = 'wms.sortingBatch';

/** 批次已结束分拣 / 终态时「创建包牌」置灰（仍可点击以提示原因，与列表行操作一致） */
const BLOCKED_BUTTON_CLASS = 'cursor-not-allowed !border-[#e5e6eb] !bg-[#f2f3f5] !text-[#c9cdd4]';

/** 包牌摘要：已创建 / 已装包 / 未使用（未使用含结束分拣作废的） */
export interface BatchBagSummary {
    created: number;
    used: number;
    unused: number;
}

interface BatchBagTableProps {
    batch: SortingBatch;
    bags: BatchBag[];
    summary: BatchBagSummary;
    loading?: boolean;
    direction: BatchDirection;
    onOpenBagDetail: (bag: BatchBag, batch: SortingBatch) => void;
    onOpenPrint: (jobs: PrintJob[]) => void;
    onOpenMoveBags: (source: MoveSource) => void;
    onOpenCreateBags: (batches: CreateBagsTarget[]) => void;
}

/**
 * 批次详情中的包牌列表：摘要（已创建 / 已装包 / 未使用）+ 勾选工具栏 + 包牌表格。
 * 包牌与摘要由详情抽屉查好传入，打印所选直接用勾选行，不再查接口。
 */
export const BatchBagTable: React.FC<BatchBagTableProps> = ({
    batch, bags, summary, loading = false, direction, onOpenBagDetail, onOpenPrint, onOpenMoveBags, onOpenCreateBags,
}) => {
    const { t } = useTranslation();
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const outbound = direction === 'OUTBOUND';

    // 已移出等不在列表中的包牌自动从勾选中剔除
    const selectedBags = useMemo(() => {
        const keys = new Set(selectedKeys);
        return bags.filter((g) => keys.has(g.bagNumber));
    }, [bags, selectedKeys]);

    const blockedReason = blockedReasonOf(batch.status);

    const handlePrint = () => {
        if (!selectedBags.length) {
            message.warning(t(`${NS}.detail.selectBagsFirst`));
            return;
        }
        onOpenPrint(toPrintJobs(selectedBags, batch));
    };

    const handleMove = () => {
        if (!selectedBags.length) {
            message.warning(t(`${NS}.detail.selectBagsFirst`));
            return;
        }
        onOpenMoveBags({ batchNumber: batch.batchNumber, destination: batch.destination, numbers: selectedBags.map((g) => g.bagNumber) });
    };

    const handleCreateBags = () => {
        if (blockedReason === 'sortingEnded') {
            message.warning(t(`${NS}.detail.blockedSortingEnded`));
            return;
        }
        if (blockedReason === 'terminated') {
            message.warning(t(`${NS}.detail.blockedTerminated`));
            return;
        }
        onOpenCreateBags([{ id: batch.id, batchNumber: batch.batchNumber, destination: batch.destination, status: batch.status }]);
    };

    const columns: ColumnsType<BatchBag> = [
        {
            title: t(`${NS}.detail.columns.bagNumber`),
            dataIndex: 'bagNumber',
            align: 'center',
            render: (_: unknown, bag) => (
                <Typography.Link onClick={() => onOpenBagDetail(bag, batch)}>
                    {bag.bagNumber}
                </Typography.Link>
            ),
        },
        {
            title: t(`${NS}.detail.columns.sortingCode`),
            dataIndex: 'sortingCode',
            align: 'center',
            render: (code: string) => code || t(`${NS}.common.none`),
        },
        {
            title: t(`${NS}.detail.columns.bagStatus`),
            dataIndex: 'status',
            align: 'center',
            width: 130,
            filters: BAG_STATUSES.map((s) => ({ text: getBagStatusText(s, t), value: s })),
            filterMultiple: false,
            onFilter: (value, bag) => bag.status === value,
            render: (_: unknown, bag) => <BagStatusTag status={bag.status} />,
        },
        {
            title: t(`${NS}.detail.columns.creationMethod`),
            dataIndex: 'creationMethod',
            align: 'center',
            width: 130,
            filters: CREATION_METHODS.map((m) => ({ text: t(`${NS}.creationMethod.${m}`), value: m })),
            filterMultiple: false,
            onFilter: (value, bag) => bag.creationMethod === value,
            render: (_: unknown, bag) => <CreationMethodTag method={bag.creationMethod} />,
        },
        {
            title: t(`${NS}.detail.columns.parcels`),
            dataIndex: 'parcels',
            align: 'center',
            // 到件解封、他仓拆空的已废弃包牌在本批次仍有包裹，照常显示件数
            render: (_: unknown, bag) => (bag.status === BagStatus.NOT_USED || (bag.status === BagStatus.DISCARDED && !bag.parcels) ? t(`${NS}.common.none`) : formatCount(bag.parcels)),
        },
    ];

    return (
        <div>
            <div className="flex flex-wrap items-center gap-2.5 pb-2.5">
                <span className="min-w-0 flex-1 truncate text-xs text-[#86909c]">
                    {t(`${NS}.detail.bagSummary`, { created: summary.created, used: summary.used, unused: summary.unused })}
                </span>
                <span className="flex flex-none items-center gap-2 whitespace-nowrap">
                    <span className="text-xs text-[#86909c]">{t(`${NS}.common.selectedCount`, { count: selectedBags.length })}</span>
                    <Button type="link" size="small" className="px-0" onClick={() => setSelectedKeys([])}>
                        {t(`${NS}.common.clear`)}
                    </Button>
                    <Button onClick={handlePrint}>{t(`${NS}.detail.printSelected`)}</Button>
                    {outbound && <Button onClick={handleMove}>{t(`${NS}.detail.moveToOtherBatch`)}</Button>}
                    {outbound && (
                        <Button
                            type={blockedReason ? 'default' : 'primary'}
                            className={blockedReason ? BLOCKED_BUTTON_CLASS : undefined}
                            aria-disabled={!!blockedReason}
                            onClick={handleCreateBags}
                        >
                            {t(`${NS}.detail.createBags`)}
                        </Button>
                    )}
                </span>
            </div>
            <Table<BatchBag>
                rowKey="bagNumber"
                size="middle"
                bordered
                loading={loading}
                columns={columns}
                dataSource={bags}
                rowSelection={{
                    selectedRowKeys: selectedBags.map((g) => g.bagNumber),
                    onChange: (keys) => setSelectedKeys(keys.map(String)),
                    // 勾选框可访问名称为包牌号（antd 类型未声明 aria-*，运行时会透传到 input）
                    getCheckboxProps: (bag) => ({ 'aria-label': bag.bagNumber } as Partial<CheckboxProps>),
                }}
                onChange={(_pagination, _filters, _sorter, extra) => {
                    // 切换筛选时清空勾选（原型行为），避免勾选被筛掉的包牌
                    if (extra.action === 'filter') setSelectedKeys([]);
                }}
                pagination={{
                    defaultPageSize: DEFAULT_PAGE_SIZE,
                    pageSizeOptions: PAGE_SIZE_OPTIONS,
                    showSizeChanger: true,
                    position: ['bottomCenter'],
                    showTotal: (total) => t(`${NS}.detail.results`, { total }),
                }}
            />
        </div>
    );
};
